import { ChangeDetectorRef, Component, Input, NgZone, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CdsButtonConfig } from '@cds/ng-core/button';
import { CdsPopupService } from '@cds/ng-web-components/popup';
import { CdsLangService, CdsLanguageModule } from '@cds/ng-core/lang';
import { ToastAlertService } from '../toast-alert.service';
import { AlertPopupComponent } from '../alert-popup/alert-popup.component';
import { ContinuePopupComponent } from '../continue-popup/continue-popup.component';
import { AmlCommentsHistoryComponent } from './aml-comments-history.component';
import { DateTime } from 'luxon';
import { PermissionAccess, PermissionItem } from 'src/app/core/models/enum/permission.enum';
import { AMLRiskSummary, AMLComment } from 'src/app/views/employee/employee';
import { AmlRiskSummaryService } from './aml-risk-summary.service';
import { CdAccordionComponent } from '../../components/accordion/cd-accordion/cd-accordion.component';
import { CdAccordionPanelComponent } from '../../components/accordion/cd-accordion-panel/cd-accordion-panel.component';
import { LoadingComponent } from '../loading/loading.component';
import { NgClass } from '@angular/common';
import { PermissionComponent } from '../permission/permission.component';
import { CdsIconModule } from '@cds/ng-web-components/icon';
import { CdsButtonModule } from '@cds/ng-web-components/button';
import { SsmsDatePipe } from '../pipe/date.pipe';
import { CdTextareaComponent } from '../cd-textarea/cd-textarea.component';

@Component({
  selector: 'app-aml-risk-summary',
  templateUrl: './aml-risk-summary.component.html',
  styleUrls: ['./aml-risk-summary.component.scss'],
  imports: [
    CdAccordionComponent,
    CdAccordionPanelComponent,
    LoadingComponent,
    NgClass,
    PermissionComponent,
    CdsIconModule,
    CdsButtonModule,
    CdsLanguageModule,
    SsmsDatePipe,
    CdTextareaComponent,
  ],
})
export class AmlRiskSummaryComponent implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  private toastAlert = inject(ToastAlertService);
  private amlService = inject(AmlRiskSummaryService);
  private cdsPopup = inject(CdsPopupService);
  private langService = inject(CdsLangService);
  private zone = inject(NgZone, { optional: true }) ?? ({ run: <T>(fn: () => T) => fn() } as NgZone);
  private readonly maxCommentLength = 1000;

  @Input() customerId?: string;
  @Input() entityType: 'EE' | 'ER' = 'EE'; // 用于区分 Employee 和 Employer

  PermissionAccess = PermissionAccess;
  PermissionItem = PermissionItem;

  isLoading = false;
  commentsShow = false;
  commentsData = '';

  amlRiskSummary?: AMLRiskSummary;
  amlComment: AMLComment = {
    comment: '',
    version: 0,
  };

  resetConfig: CdsButtonConfig = {
    size: 'sm',
    style: 'secondary',
    disabled: true,
  };

  confirmConfig: CdsButtonConfig = {
    size: 'sm',
    disabled: true,
  };

  ngOnInit(): void {
    if (this.customerId) {
      this.loadAMLData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customerId']?.currentValue !== undefined && changes['customerId']?.currentValue !== changes['customerId']?.previousValue) {
      this.loadAMLData();
    }
  }

  loadAMLData(): void {
    if (!this.customerId) return;

    this.isLoading = true;

    // 加载 AML Risk Summary (包含latestComment) - 传入entityType
    this.amlService.getAMLRiskSummary(this.customerId, this.entityType).subscribe({
      next: (response: { result: number; data?: AMLRiskSummary }) => {
        this.runInUi(() => {
          this.isLoading = false;
          if (response.result === 0 && response.data) {
            this.amlRiskSummary = response.data;
            // 使用后端返回的latestComment
            if (this.amlRiskSummary?.latestComment) {
              this.amlComment = this.amlRiskSummary.latestComment;
            } else {
              this.amlComment = { comment: '', version: 0 };
            }
          } else {
            // 即使没有AML数据,也允许评论
            this.amlRiskSummary = undefined;
            this.amlComment = { comment: '', version: 0 };
          }
          this.setCommentValue(this.amlComment.comment || '');
          // 默认不打开编辑框
          this.commentsShow = false;
        });
      },
      error: () => {
        this.runInUi(() => {
          this.isLoading = false;
          // 即使加载失败,也允许评论
          this.amlRiskSummary = undefined;
          this.amlComment = { comment: '', version: 0 };
          this.setCommentValue('');
          // 默认不打开编辑框
          this.commentsShow = false;
        });
      },
    });
  }

  // 不再需要单独的loadLatestComment方法,因为latestComment已经在AMLRiskSummaryResponse中

  editTrigger(): void {
    if (this.commentsShow && !this.resetConfig.disabled) {
      const dialogRef = this.cdsPopup.open(ContinuePopupComponent, {
        size: 'sm',
        data: { message: this.langService.translate('common.action.cancel.italic') },
      });

      dialogRef.beforeClosed().subscribe(result => {
        if (result?.agree) {
          this.scheduleCommentStateUpdate(() => {
            this.commentsShow = false;
            this.setCommentValue(this.amlComment?.comment || '');
          });
        }
      });
    } else {
      this.setCommentValue(this.amlComment?.comment || '');
      this.commentsShow = !this.commentsShow;
    }
  }

  showCommentsLog(): void {
    if (!this.customerId) return;

    this.cdsPopup.open(AmlCommentsHistoryComponent, {
      size: 'lg',
      data: {
        customerId: this.customerId,
        entityType: this.entityType,
      },
    });
  }

  reset(): void {
    if (this.resetConfig.disabled) return;

    const dialogRef = this.cdsPopup.open(ContinuePopupComponent, {
      size: 'sm',
      data: { message: this.langService.translate('common.action.cancel.italic') },
    });

    dialogRef.beforeClosed().subscribe(result => {
      if (result?.agree) {
        this.scheduleCommentStateUpdate(() => {
          this.setCommentValue(this.amlComment?.comment || '');
        });
      }
    });
  }

  confirm(): void {
    if (!this.customerId) return;
    if (this.confirmConfig.disabled) return;

    const request = {
      customerId: this.customerId,
      comment: this.commentsData,
      version: this.amlComment.version || 0,
    };

    this.isLoading = true;
    this.amlService.submitComment(request, this.entityType).subscribe({
      next: (data: { result: number; data?: AMLComment; message?: string }) => {
        this.runInUi(() => {
          this.isLoading = false;
          if (data.result === 0) {
            // 先本地同步最新评论，避免等待重新查询时页面不刷新。
            this.amlComment = {
              ...(this.amlComment || {}),
              ...(data.data || {}),
              comment: this.commentsData,
            };

            this.cdsPopup
              .open(AlertPopupComponent, {
                size: 'sm',
                data: {
                  message: this.langService.translate('common.action.success.italic'),
                  buttonName: this.langService.translate('common.gotItWithExclamation'),
                },
              })
              .afterClosed()
              .subscribe(() => {
                this.runInUi(() => {
                  this.commentsShow = false;
                  this.setCommentValue(this.commentsData);
                  // 重新加载AML数据以获取最新的latestComment
                  this.loadAMLData();
                });
              });
          } else {
            this.toastError(data.message || 'Failed to submit comment');
          }
        });
      },
      error: (error: { error?: { message?: string } }) => {
        this.runInUi(() => {
          this.isLoading = false;
          const message = error?.error?.message || 'Failed to submit comment';
          this.toastError(message);
        });
      },
    });
  }

  commentInput(value: string): void {
    this.validating(value);
  }

  validating(val: string): void {
    this.setCommentValue(val);
  }

  private setCommentValue(value: string): void {
    const normalizedValue = this.normalizeComment(value);
    const originalComment = this.normalizeComment(this.amlComment?.comment || '');

    this.commentsData = normalizedValue;

    this.resetConfig = {
      size: 'sm',
      style: 'secondary',
      disabled: originalComment === normalizedValue,
    };

    this.confirmConfig = {
      size: 'sm',
      disabled: originalComment === normalizedValue,
    };
  }

  private normalizeComment(value: string): string {
    return (value || '').replace(/[\u4e00-\u9fa5]/g, '').slice(0, this.maxCommentLength);
  }

  private scheduleCommentStateUpdate(callback: () => void): void {
    requestAnimationFrame(() => {
      this.runInUi(() => {
        callback();
      });
    });
  }

  updateDateGen(value?: string): string {
    if (!value) return '';
    const dateTime = DateTime.fromISO(value);
    if (dateTime.isValid) {
      return dateTime.toFormat('hh:mma dd/MM/yyyy (Z') + 'T)';
    }
    return '';
  }

  hasAMLData(): boolean {
    if (!this.amlRiskSummary) return false;
    // 检查是否有任何 AML Type 数据
    return !!(
      this.amlRiskSummary.amlTyp1 ||
      this.amlRiskSummary.amlTyp2 ||
      this.amlRiskSummary.amlTyp3 ||
      this.amlRiskSummary.amlRskLvl ||
      this.amlRiskSummary.usIndiciaFlg
    );
  }

  toastError(errorMessage: string): void {
    this.toastAlert.show('error', 'common.error', errorMessage, 5000);
  }

  private runInUi(work: () => void): void {
    this.zone.run(() => {
      work();
      this.safeDetectChanges();
    });
  }

  private safeDetectChanges(): void {
    if (!(this.cdr as any)?.destroyed) {
      this.cdr.detectChanges();
    }
  }
}
